import * as config from '../config';
import BaseView, { DomainModel3D } from './base-view';
import ModelLoader from '../model-loader';
import Signal from '../control-flow/signal';
import assets from '../assets';
import logging from '../logging';
import { Handle } from '../domain';
import { Mesh, Object3D } from 'three';
import { getParentView } from '../controllers/base-view-controllers';
import { zip } from '../control-flow/zip';

const log = logging('views');

const ml = ModelLoader.getInstance();


export default class HandleView extends BaseView {

    declare model: Handle;
    handle: Object3D;

    isNonRotating: boolean = false;

    geometryAvailable: boolean = false;
    onGeometryAvailable = new Signal<[HandleView]>("onGeometryAvailable", "views");

    constructor(model: DomainModel3D) {
        super(model);
        this.init();
        this.setGlbMaterialOpacity = this.setGlbMaterialOpacity.bind(this);

        this.onReady.name = 'HandleView';
    }

    make(): void {
        // Handles are not parametric, so there's no placeholder stub to
        // instantiate here.
    }

    bind(): void {
        const changeStyle = this.changeStyle.bind(this);
        this.model.bind('style', changeStyle);
        this.model.on('opacity', () => {
            const castShadow = this.model.opacity == 1;
            this.traverse(obj => {
                if(obj["isMesh"]) obj.castShadow = castShadow;
            });
        });
    }

    changeStyle() {
        this.updates.queue(
            () => this.loadHandle(this.model),
            (handle: Object3D) => {
                this.handle?.removeFromParent();
                this.handle = handle;
                this.add(this.handle);

                this.highlights.clear();
                this.handle.traverse(obj => {
                    if(obj instanceof Mesh) {
                        this.highlights.add(obj);
                    }
                });

                const castShadow = this.model.opacity == 1;
                this.traverse(obj => {
                    if(obj["isMesh"]) obj.castShadow = castShadow;
                });

                this.geometryAvailable = true;
                this.onGeometryAvailable.trigger(this);
                this.onReady.trigger();
            });
    }

    /**
     * Loads and sets up materials for generic assets.
     */
    async loadHandle(handle: Handle) {
        let path: string;
        if(handle.assetId) {
            path = assets.resolve(handle.assetId, 'LowResModel');
        }
        else {
            path = `${config.models}/${handle.style}`;
        }
        const object = await ml.get(path);
        const suffix = path.split('.').pop().toLowerCase();
        const parentView = getParentView(this.parent);

        if (suffix === "obj") {

            object.traverse(child => {
                if(child instanceof Mesh && child.geometry) {
                    child.castShadow = child.receiveShadow = true;
                    parentView.highlights.add(child);
                    if (handle.materials.length > 1) {
                        this.bindMaterialArray(child, handle.materials);
                    } else {
                        this.bindMaterial(child, handle.materials[0]);
                    }
                    child.material.needsUpdate = true;
                }
            });

        }
        else if(suffix === "glb"
                  && handle.materials
                  && handle.materials.length > 0)
        {
            const children = [];
            object.traverse(child => {
                if(child instanceof Mesh && child.geometry) {
                    if(child.userData["cps_pull_non_rotating"]) {
                        this.isNonRotating = true;
                    }
                    child.castShadow = child.receiveShadow = true;
                    parentView.highlights.add(child);
                    children.push(child);
                }
            });
            for(let [obj, mat] of zip(children, handle.materials)) {
                this.bindMaterial(obj, mat);
            }
        }
        else if(suffix == "glb") {
            object.traverse(child => {
                if(child instanceof Mesh && child.geometry) {
                    child.castShadow = child.receiveShadow = true;
                    parentView.highlights.add(child);
                }
            });
            handle.off('opacity', this.setGlbMaterialOpacity);
            handle.on('opacity', this.setGlbMaterialOpacity);
        }

        return object;
    };

    setGlbMaterialOpacity() {
        this.handle.traverse(obj => {
            if(obj instanceof Mesh) {
                obj.material.transparent = true;
                obj.material.opacity = this.model.opacity;
            }
        });
    }
}
